'use client';

import { useState } from 'react';
import Link from 'next/link';
import { sectionIds } from '@/content/site';
import type { Dictionary } from '@/i18n/dictionary';
import { ordinal } from '@/lib/format';
import { cn } from '@/lib/cn';
import { DirectionVisual } from '@/components/ui/direction-visual';
import { Reveal } from '@/components/ui/reveal';
import { SectionIntro } from '@/components/ui/section-intro';

export function Directions({ dictionary }: { dictionary: Dictionary }) {
  const { directions } = dictionary;
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id={sectionIds.directions} className="border-t border-line py-section">
      <div className="container-page">
        <SectionIntro
          index="04"
          title={directions.title}
          intro={directions.intro}
          titleClassName="max-w-none"
        />

        <div className="mt-[clamp(48px,6vw,88px)] border-t border-ink">
          {directions.items.map((item, index) => {
            const open = openIndex === index;
            const panelId = `direction-panel-${index}`;

            return (
              <Reveal key={item.title} delay={index * 80} className="border-b border-line">
                <h3>
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-controls={panelId}
                    onClick={() => setOpenIndex(open ? null : index)}
                    className={cn(
                      'grid w-full grid-cols-[72px_minmax(0,1fr)_48px] items-center gap-4 py-[clamp(22px,2.4vw,32px)] text-left transition-colors duration-250 hover:text-accent',
                      open && 'text-accent',
                    )}
                  >
                    <span className="text-[13px] font-semibold tracking-[0.06em] text-accent">
                      {ordinal(index)}
                    </span>
                    <span className="text-heading font-light">{item.title}</span>
                    <span
                      aria-hidden
                      className={cn(
                        'inline-flex size-12 items-center justify-center rounded-full border border-line transition-transform duration-450 ease-soft',
                        open && 'rotate-45 bg-canvas',
                      )}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 0V14M0 7H14" stroke="currentColor" strokeWidth={1.5} />
                      </svg>
                    </span>
                  </button>
                </h3>

                <div
                  id={panelId}
                  className="grid transition-[grid-template-rows] duration-500 ease-soft"
                  style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
                >
                  <div className="min-h-0 overflow-hidden">
                    <div
                      inert={!open}
                      className={cn(
                        'grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-x-16 gap-y-8 pt-1 pb-[clamp(32px,4vw,56px)] transition-opacity delay-100 duration-400',
                        open ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      <div className="flex flex-col gap-6 pl-[min(88px,8vw)]">
                        <p className="max-w-[50ch] text-body">{item.description}</p>
                        <ul className="flex flex-wrap gap-2">
                          {item.tags.map((tag) => (
                            <li
                              key={tag}
                              className="rounded-full border border-line px-3.5 py-2 text-[13px] font-medium"
                            >
                              {tag}
                            </li>
                          ))}
                        </ul>
                        <Link
                          href={`#${sectionIds.contact}`}
                          className="inline-flex min-h-11 items-center gap-2.5 self-start border-b border-ink text-[15px] font-medium transition-colors hover:border-accent hover:text-accent"
                        >
                          {item.cta}
                          <span aria-hidden>→</span>
                        </Link>
                      </div>

                      <div className="flex items-start justify-end">
                        <div className="relative flex aspect-3/2 w-full max-w-[420px] items-center justify-center overflow-hidden rounded-2xl border border-line bg-surface">
                          <DirectionVisual index={index} />
                          <span className="absolute bottom-3 left-3.5 text-[11px] tracking-[0.08em] text-muted uppercase">
                            {directions.visual}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
